import type { AxiosError } from 'axios';
import type { NavigationGuardNext, RouteLocationNormalized, RouteMeta } from 'vue-router';

import { useLoginFlowPlugin } from '@/plugins/login-flow';
import { isRequestError } from '@/plugins/request/index.js';
import { RouteNames } from '@/router/routeNames';
import type { AclModel } from '@/shared/layouts/base/models';
import type { UserType } from '@/shared/services/user/models';
import { useUserContextStore } from '@/shared/user-context/stores';

type AclEntry = () => AclModel;

type Entry = AclEntry | AclModel;

type MetaAcl = RouteMeta & {
  [key]?: AclEntry;
};

const key = 'acl-meta';

export function useAclMeta(entry: Entry): MetaAcl {
  return {
    [key]: () => {
      return entry instanceof Function ? entry() : entry;
    },
  };
}

export async function useRouterAclGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const aclInfo = extractAclInfoFromRoutes(to);

  if (!aclInfo.requireAuth) {
    return next();
  }

  const $loginFlow = useLoginFlowPlugin();

  if (!$loginFlow.state.isLogin) {
    return $loginFlow.login(to.name as string);
  }

  const $userContextStore = useUserContextStore();

  try {
    await $userContextStore.fetchUser();
  } catch (error) {
    if (isRequestError(error) && (error as AxiosError).response?.status === 403) {
      return next({ name: RouteNames.FORBIDDEN });
    }
  }

  if (aclInfo.allowedUserTypes && aclInfo.allowedUserTypes.length > 0) {
    if ($userContextStore.userType && aclInfo.allowedUserTypes.includes($userContextStore.userType)) {
      return next();
    }

    return next({ name: RouteNames.NOT_FOUND });
  }

  return next();
}

function extractAclInfoFromRoutes(route: RouteLocationNormalized): AclModel {
  let requireAuth = false;

  const allowedUserTypes = new Set<UserType>();

  for (const matchedRoute of route.matched) {
    const aclMetaFunction = matchedRoute.meta?.[key] as AclEntry | undefined;

    if (aclMetaFunction) {
      try {
        const acl = aclMetaFunction();

        if (acl.requireAuth) {
          requireAuth = true;
        }

        if (acl.allowedUserTypes) {
          acl.allowedUserTypes.forEach((userType) => allowedUserTypes.add(userType));
        }
      } catch (error) {
        console.warn('Error parsing ACL meta for route:', matchedRoute.name, error);
      }
    }
  }

  return {
    requireAuth,
    allowedUserTypes: Array.from(allowedUserTypes),
  };
}
